import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  DialogTitle,
  DialogContent,
  DialogActions,
} from '../../../common/Dialog';

import FieldSet, {
  TextField,
  FieldRow,
  Checkbox,
} from '../../../common/FieldSets';

const GroupSetInner = ({
  mode,
  initialName,
  initialIsHidden,
  isLocked,
  saving,
  onSave,
  onClose,
  deleting,
  onDelete,
}) => {
  const [name, setName] = useState(initialName);
  const [isHidden, setIsHidden] = useState(initialIsHidden);

  const canSave = useMemo(() => name.length > 0, [name]);

  const handleNameChange = useCallback((event) => setName(event.target.value), [
    name,
  ]);
  const handleHideChange = useCallback(
    (event) => setIsHidden(event.target.checked),
    [isHidden]
  );
  const handleSave = useCallback(() => {
    onSave({ name, isHidden });
  }, [name, isHidden]);

  const title = useMemo(() => (mode === 'create' ? 'New set' : 'Edit set'), [
    mode,
  ]);

  const secondary = useMemo(
    () =>
      mode === 'edit' && !isLocked
        ? {
            className: 'delete-set-button',
            label: 'Delete',
            onClick: onDelete,
            disabled: saving,
            loading: deleting,
            confirmation: {
              warningLabel:
                'Are you sure?  Deleting this set removes all associated groups as well.',
              acceptLabel: 'Yes',
              cancelLabel: 'No',
            },
          }
        : undefined,
    [mode, saving, deleting, isLocked]
  );

  return (
    <React.Fragment>
      <DialogTitle id={`${mode}-set-dialogtitle`} onClose={onClose}>
        {title}
      </DialogTitle>
      <DialogContent>
        <FieldSet title="Set information">
          <FieldRow>
            <TextField
              id="set-name"
              label="Name"
              value={name}
              onChange={handleNameChange}
            />
          </FieldRow>
          <FieldRow>
            <Checkbox
              id="set-hide"
              label="Hide set"
              checked={isHidden}
              onChange={handleHideChange}
              disabled={isLocked}
            />
          </FieldRow>
        </FieldSet>
      </DialogContent>
      <DialogActions
        primary={{
          className: 'save-set-button',
          label: 'Save',
          disabled: !canSave,
          onClick: handleSave,
          loading: saving,
        }}
        secondary={secondary}
      />
    </React.Fragment>
  );
};

GroupSetInner.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  initialName: PropTypes.string,
  initialIsHidden: PropTypes.bool,
  isLocked: PropTypes.bool,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
  onDelete: PropTypes.func,
};

GroupSetInner.defaultProps = {
  initialName: '',
  initialIsHidden: false,
  isLocked: false,
  saving: false,
  deleting: false,
  onDelete: () => {},
};

export default GroupSetInner;
